import { productVariantSeparator } from '../../entities/@products/Product/Product';
import { getCurrentVersion, getLatestVersion } from '../../entities/App/AppService';
import { isFetchResultSuccessful } from '../../entities/FetchResult/FetchResult';
import { AppRoute } from '../../entities/Routing/Routing';
import { getAppRoute, getRouteParam } from '../../helpers/routing';
import { getFileExtensionFromUrl } from '../../helpers/url';
import { ReduxFetchAction } from '../defaults';
import {
    setCurrentVersion,
    setError,
    setIsLoading,
    setLatestVersion,
    setPageNotFound,
} from './appSlice';

export const fetchAppVersions: ReduxFetchAction = () => async dispatch => {
    dispatch(setIsLoading(true));
    dispatch(setError(''));

    try {
        const currentVersionResponse = await getCurrentVersion();
        const latestVersionResponse = await getLatestVersion();

        if (!isFetchResultSuccessful(currentVersionResponse)) {
            dispatch(setError(currentVersionResponse.error));
            return;
        }

        if (!isFetchResultSuccessful(latestVersionResponse)) {
            dispatch(setError(latestVersionResponse.error));
            return;
        }

        const currentVersion = currentVersionResponse.data;
        const latestVersion = latestVersionResponse.data;

        dispatch(setCurrentVersion(currentVersion));
        dispatch(setLatestVersion(latestVersion));
    } catch (error) {
        console.error('[fetchAppVersions]', error);
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const checkIfRequestIsNotFound: ReduxFetchAction<string> = path => async (dispatch, getState) => {
    let pageNotFound = false;

    const {
        blogCategoriesSlice,
        blogPostSlice,
        brandSlice,
        storeSlice,
        departmentDetailSlice,
        pagesSlice,
        customPageSlice,
        customPagesSlice,
        productCategoriesSlice,
        productCategorySlice,
        productSlice,
        spaceSlice,
    } = getState();

    const appRoute = getAppRoute(path);
    const isHomePage = path === '/';
    const isFile = !!getFileExtensionFromUrl(path);

    const slug = path.slice(1);
    const slugParts = slug.split('/');

    const productCategorySlugs = productCategoriesSlice.productCategories.map(productCategory => productCategory.slug);

    const blogCategory = getRouteParam({ path, appRoute: AppRoute.blogOverviewCategory, param: ':category' });
    const blogPostSlug = getRouteParam({ path, appRoute: AppRoute.blogPost });
    const brandDetailSlug = getRouteParam({ path, appRoute: AppRoute.brandDetail });
    const spaceDetailSlug = getRouteParam({ path, appRoute: AppRoute.spaceDetail });
    const storeDetailSlug = getRouteParam({ path, appRoute: AppRoute.storeDetail });
    const departmentDetailSlug = getRouteParam({ path, appRoute: AppRoute.departmentDetail });
    const ownCollectionCategorySlug = getRouteParam({ path, appRoute: AppRoute.ownCollectionCategory, param: ':categorySlug' });

    if (appRoute) {
        const { hasFetched, pages } = pagesSlice;
        pageNotFound = hasFetched && !pages[appRoute];
    }

    if (blogCategory) {
        const { hasFetched, categories } = blogCategoriesSlice;
        pageNotFound = hasFetched && !categories.includes(blogCategory);
    }

    if (blogPostSlug) {
        const { hasFetched, blogPost } = blogPostSlice;
        pageNotFound = hasFetched && !blogPost;
    }

    if (brandDetailSlug) {
        const { hasFetched, brand } = brandSlice;
        pageNotFound = hasFetched && !brand;
    }

    if (spaceDetailSlug) {
        const { hasFetched, space } = spaceSlice;
        pageNotFound = hasFetched && !space;
    }

    if (storeDetailSlug) {
        const { hasFetched, store } = storeSlice;
        pageNotFound = hasFetched && !store;
    }

    if (departmentDetailSlug) {
        const { hasFetched, departmentDetail } = departmentDetailSlice;
        pageNotFound = hasFetched && !departmentDetail;
    }

    const isOtherPage = ![
        isHomePage,
        !!appRoute,
        !!blogCategory,
        !!blogPostSlug,
        !!brandDetailSlug,
        !!spaceDetailSlug,
        !!storeDetailSlug,
        !!departmentDetailSlug,
        !!ownCollectionCategorySlug,
    ].some(Boolean);

    const isCustomPage = !isFile && customPagesSlice.slugs.includes(slug);
    const allSlugsAreCategories = slugParts.every(part => productCategorySlugs.includes(part));
    const isProductCategory = !isFile && !isCustomPage && allSlugsAreCategories;
    const isNonExistentProductCategory = isOtherPage && !isProductCategory && slugParts.length > 1;
    const isProductDetail = !isFile && !isCustomPage && !isProductCategory && slug.includes(productVariantSeparator);

    if (isOtherPage) {
        pageNotFound = !isCustomPage && !isProductCategory && !isNonExistentProductCategory && !isProductDetail;
    }

    if (isCustomPage) {
        const { hasFetched, pageData } = customPageSlice;
        pageNotFound = hasFetched && !pageData;
    }

    if (isProductCategory || isNonExistentProductCategory) {
        const { hasFetched, pageData } = productCategorySlice;
        pageNotFound = hasFetched && !pageData;
    }

    if (isProductDetail) {
        const { hasFetched, productDefault, productVariant } = productSlice;
        pageNotFound = hasFetched && (!productDefault || !productVariant);
    }

    // Set final value in store
    dispatch(setPageNotFound(pageNotFound));
};
